'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';

type Language = 'en' | 'ar';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('en');
  const [translations, setTranslations] = useState<Record<string, string>>({});

  // Removed localStorage check - always default to English
  // useEffect(() => {
  //   const savedLang = localStorage.getItem('language') as Language;
  //   if (savedLang && (savedLang === 'en' || savedLang === 'ar')) {
  //     setLanguageState(savedLang);
  //   }
  // }, []);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const module = await import(`@/locales/${language}.json`);
        setTranslations(module.default);
      } catch (error) {
        console.error('Failed to load translations:', error);
      }
    };
    loadTranslations();
    
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    return translations[key] || key;
  };

  return (
    <LanguageContext.Provider data-component-id="src/contexts/LanguageContext.tsx:64:4" data-component-path="src/contexts/LanguageContext.tsx" data-component-line="64" data-component-end-line="66" data-component-file="LanguageContext.tsx" data-component-name="LanguageContext.Provider" data-component-content="%7B%22elementName%22%3A%22LanguageContext.Provider%22%7D" value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};